import {
  customElement,
  html,
  property,
  css,
  cssManager,
  state,
  type TemplateResult,
} from '@design.estate/dees-element';
import { DeesInputBase } from '../dees-input-base/dees-input-base.js';
import '../../00group-utility/dees-icon/dees-icon.js';
import '../../00group-layout/dees-label/dees-label.js';
import { ProfilePictureModal } from './profilepicture.modal.js';
import { demoFunc } from './dees-input-profilepicture.demo.js';
import { themeDefaultStyles } from '../../00theme.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-input-profilepicture': DeesInputProfilePicture;
  }
}

export type ProfileShape = 'square' | 'round';

@customElement('dees-input-profilepicture')
export class DeesInputProfilePicture extends DeesInputBase<DeesInputProfilePicture> {
  public static inputLayout = 'compound' as const;
  public static demo = demoFunc;
  public static demoGroups = ['Input'];

  @property({ type: String })
  accessor value: string = ''; // Base64 encoded image or URL

  @property({ type: String })
  accessor shape: ProfileShape = 'round';

  @property({ type: Number })
  accessor size: number = 120;

  @property({ type: String })
  accessor placeholder: string = '';

  @property({ type: Boolean })
  accessor allowUpload: boolean = true;

  @property({ type: Boolean })
  accessor allowDelete: boolean = true;

  @property({ type: Number })
  accessor maxFileSize: number = 5 * 1024 * 1024; // 5MB

  @property({ type: Array })
  accessor acceptedFormats: string[] = ['image/jpeg', 'image/png', 'image/webp'];

  @property({ type: Number })
  accessor outputSize: number = 800; // Output resolution in pixels

  @property({ type: Number })
  accessor outputQuality: number = 0.95; // 0-1 quality for JPEG

  @state()
  accessor isHovered: boolean = false;

  @state()
  accessor isDragging: boolean = false;

  @state()
  accessor isLoading: boolean = false;

  private modalInstance: ProfilePictureModal | null = null;

  public static styles = [
    themeDefaultStyles,
    ...DeesInputBase.baseStyles,
    cssManager.defaultStyles,
    css`
      :host { display: block; min-width: 0; }
      .input-wrapper { display: block; }
      .profile-container { display: flex; align-items: center; flex-wrap: wrap; gap: 16px; }
      .profile-picture {
        box-sizing: border-box; width: var(--size, 120px); height: var(--size, 120px);
        padding: 0; background: var(--dees-color-fill-tertiary);
        border: 1px solid var(--dees-color-border-default); color: var(--dees-color-text-muted);
        display: flex; align-items: center; justify-content: center; overflow: hidden;
        position: relative; flex: none; cursor: pointer;
      }
      .profile-picture.round { border-radius: 50%; }
      .profile-picture.square { border-radius: var(--dees-radius-xl); }
      .profile-picture:focus-visible, .profile-picture.dragging {
        outline: 2px solid var(--dees-color-focus-ring); outline-offset: 2px;
      }
      .profile-picture:disabled { cursor: default; opacity: .5; }
      .profile-image { width: 100%; height: 100%; object-fit: cover; }
      .picture-actions { display: flex; flex-wrap: wrap; gap: 8px; }
      .picture-actions button { padding: 0 10px; gap: 6px; min-height: var(--dees-input-height); }
      .picture-actions .remove { color: var(--dees-color-text-secondary); }
      .drop-zone-text { position: absolute; inset: 0; display: grid; place-items: center; background: var(--dees-color-control-bg); font: 500 13px var(--dees-font-family); }
      .hidden-input { display: none; }
    `,
  ];

  render(): TemplateResult {
    return html`
      <div class="input-wrapper">
        <dees-label .label=${this.label} .infoText=${this.infoText} .required=${this.required}></dees-label>
        
        <div class="profile-container" @dragover=${this.handleDragOver}
          @dragleave=${this.handleDragLeave} @drop=${this.handleDrop} style="--size: ${this.size}px">
          <button type="button" class="profile-picture ${this.shape} ${this.isDragging ? 'dragging' : ''}"
            aria-label=${this.value ? 'Change profile picture' : 'Choose profile picture'}
            ?disabled=${this.disabled || !this.allowUpload} @click=${this.handleClick}>
            ${this.value ? html`<img class="profile-image" src=${this.value} alt="Profile picture" />`
              : html`<dees-icon icon="lucide:User" iconSize=${this.size * .4}></dees-icon>`}
            ${this.isDragging ? html`<span class="drop-zone-text">Drop image here</span>` : ''}
          </button>
          <div class="picture-actions">
            ${this.allowUpload ? html`<button type="button" class="input-action" ?disabled=${this.disabled} @click=${this.handleClick}>
              <dees-icon icon="lucide:Camera" iconSize="16"></dees-icon>${this.value ? 'Change picture' : 'Choose picture'}
            </button>` : ''}
            ${this.value && this.allowDelete ? html`<button type="button" class="input-action remove" ?disabled=${this.disabled} @click=${this.deletePicture}>Remove</button>` : ''}
          </div>
        </div>

        <input
          type="file"
          class="hidden-input"
          accept="${this.acceptedFormats.join(',')}"
          @change=${this.handleFileSelect}
          @cancel=${() => { this.isLoading = false; }}
        />
        ${this.renderDescription()}
      </div>
    `;
  }

  private handleClick(): void {
    if (this.disabled || !this.allowUpload) return;
    
    if (this.value) {
      void this.openModal();
    } else {
      this.isLoading = true;
      this.shadowRoot!.querySelector<HTMLInputElement>('.hidden-input')!.click();
    }
  }

  private handleFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    
    // Always reset loading state when file dialog interaction completes
    this.isLoading = false;
    
    if (file) {
      this.processFile(file);
    }
    
    // Reset input to allow selecting the same file again
    input.value = '';
  }

  private handleDragOver(event: DragEvent): void {
    event.preventDefault();
    if (!this.disabled && this.allowUpload) {
      this.isDragging = true;
    }
  }

  private handleDragLeave(): void {
    this.isDragging = false;
  }

  private handleDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    
    if (this.disabled || !this.allowUpload) return;
    
    const file = event.dataTransfer?.files[0];
    if (file) {
      this.processFile(file);
    }
  }

  private async processFile(file: File): Promise<void> {
    // Validate file type
    if (!this.acceptedFormats.includes(file.type)) {
      console.error('Invalid file type:', file.type);
      return;
    }
    
    // Validate file size
    if (file.size > this.maxFileSize) {
      console.error('File too large:', file.size);
      return;
    }
    
    // Read file as base64
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target?.result as string;
      
      // Open modal for cropping
      await this.openModal(base64);
    };
    reader.readAsDataURL(file);
  }

  private async openModal(initialImage?: string): Promise<void> {
    const imageToEdit = initialImage || this.value;
    
    if (!imageToEdit) {
      // If no image provided, open file picker
      const input = this.shadowRoot!.querySelector('.hidden-input') as HTMLInputElement;
      input.click();
      return;
    }
    
    // Create and show modal
    this.modalInstance = new ProfilePictureModal();
    this.modalInstance.shape = this.shape;
    this.modalInstance.initialImage = imageToEdit;
    this.modalInstance.outputSize = this.outputSize;
    this.modalInstance.outputQuality = this.outputQuality;
    
    this.modalInstance.addEventListener('save', ((event: CustomEvent) => {
      this.value = event.detail.croppedImage;
      this.changeSubject.next(this);
    }) as EventListener);
    
    document.body.appendChild(this.modalInstance);
  }

  private deletePicture(): void {
    if (this.disabled || !this.allowDelete) return;
    this.value = '';
    this.changeSubject.next(this);
  }

  public getValue(): string {
    return this.value;
  }

  public setValue(value: string): void {
    this.value = value;
  }
}
